import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  CircularProgress,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { UserNoteGet } from 'api/models/UserNoteGet';
import {
  AttioQueryKeys,
  COLUMNS_HIDDEN_BY_DEFAULT,
  COMPANY_NAME_KEY,
  FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT,
} from 'common/constants/analyst-dashboards';
import { useLocalStorageByUser } from 'common/hooks';
import { LoadingSection } from 'components';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem, TertiaryNavigation, TertiaryNavigationAddButton, TertiaryNavigationIconButton } from 'components/ui';
import type { TertiaryNavigationItem } from 'components/ui/TertiaryNavigation/TertiaryNavigation.types';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useEventSubscription } from 'context/EventBusContext/useEventSubscription';
import { useZoom } from 'context/ZoomContext';
import { ExpandableSearch, ExpandableSearchHandle } from 'dashboard409a/components/ExpandableSearch';
import ValuationsNewViewDialog from 'dashboard409a/components/ValuationsNewViewDialog';
import { calculateTableHeight, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import { DealStageOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import WorkboardSpreadsheet from 'dashboard409a/components/WorkboardSpreadsheet';
import WorkboardUserSelector from 'dashboard409a/components/WorkboardUserSelector';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { useAttioFilters } from 'dashboard409a/hooks/useAttioFilters';
import { useJobsColorPalette } from 'dashboard409a/hooks/useJobsColorPalette';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { useWorkboardValuationsTableViews } from 'dashboard409a/hooks/useWorkboardValuationsTableViews';
import { valuationsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { DEFAULT_NEW_VIEW_NAME } from 'dashboard409a/pages/Workboard/common/constants';
import {
  getDealStatusOptions,
  getValuationStatusOptions,
  updateValuationDealStage,
} from 'dashboard409a/services/attio';
import { CreateNoteRequest, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import type { ValuationView } from 'dashboard409a/types/workboardTableViews/types';
import {
  DEFAULT_COLUMN_ORDER,
  resolveColumnOrder,
} from 'dashboard409a/utilities/valuationsViewConverter';
import { useDebouncedValue } from 'hooks';
import { Logger } from 'utilities/logger';
import ConfigToggler from './ConfigToggler/ConfigToggler';
import {
  ValuationsTableBulkDataProps,
  ValuationsTableValuationUpdatedPayload,
} from './ValuationsTable.types';

const DEFAULT_TERTIARY_ITEM: TertiaryNavigationItem = { label: 'Default', value: 'default', showMenu: false };

export const ValuationsTableBulkData = ({
  filters,
  orderBy,
  order,
  sortCriteria,
  onRequestSort,
  onRowClick,
  onVisibleValuationsChange,
  sectionId,
  pauseBatchLoading = false,
  toolbarPrefix,
  hideTable = false,
}: ValuationsTableBulkDataProps) => {
  // Mount early so cachedJobsColorPalette is warmed before grid rows render.
  const { isReady: isPaletteReady } = useJobsColorPalette();

  const searchRef = useRef<ExpandableSearchHandle>(null);
  const [searchParam, setSearchParam] = useState({ searchParam: '', searchAll: false });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const debouncedSearchParam = useDebouncedValue(searchParam.searchParam, 300);

  const {
    views,
    isReady: isViewsReady,
    createView,
    updateView,
    deleteView,
    cloneView,
  } = useWorkboardValuationsTableViews(sectionId);

  const defaultNewViewName = useMemo(() => {
    const lowerNames = views.map(v => v.name.toLowerCase());
    const base = DEFAULT_NEW_VIEW_NAME;
    if (!lowerNames.includes(base.toLowerCase())) {
      return base;
    }
    const firstCandidate = `${base} (Copy)`;
    if (!lowerNames.includes(firstCandidate.toLowerCase())) {
      return firstCandidate;
    }
    let i = 2;
    while (lowerNames.includes(`${base} (Copy ${i})`.toLowerCase())) {
      i++;
    }
    return `${base} (Copy ${i})`;
  }, [views]);
  const activeViewStorageKey = sectionId ? `active-valuation-view-${sectionId}` : 'active-valuation-view';
  const [activeViewId, setActiveViewId, isActiveViewReady] = useLocalStorageByUser<string>(activeViewStorageKey, 'default');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [pendingView, setPendingView] = useState<ValuationView | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuViewId, setMenuViewId] = useState<string | null>(null);
  const [restoredSectionId, setRestoredSectionId] = useState<string | undefined>(undefined);
  const hasRestoredActiveView = restoredSectionId === sectionId;

  const [groupByDeals, setGroupByDeals, isGroupByDealsReady] = useLocalStorageByUser(
    'groupByDeals',
    false
  );
  // Remember which deal groups are collapsed across sessions
  const [collapsedDealIds, setCollapsedDealIds, areCollapsedDealsReady] = useLocalStorageByUser<string[]>(
    'collapsedDealIds',
    []
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.expand();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const {
    rows,
    groupedRows,
    totalCount: totalCountFromHook,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    updateValuationOtherConsultants,
    selectedUserEmail,
    setSelectedUserEmail,
    staffUsers,
    isLoadingStaffUsers,
    isPreferenceReady: isSelectedUserReady,
    refetch: refetchValuations,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
    sortCriteria,
    pauseBatchLoading,
    searchParams: {
      searchParam: debouncedSearchParam,
      searchAll: debouncedSearchParam.trim() ? searchParam.searchAll : false,
    },
    groupByDeals,
  });

  useEffect(() => {
    if (isRefreshing && !isLoading) {
      setIsRefreshing(false);
    }
  }, [isLoading, isRefreshing]);

  // Track color palette changes to force re-render when Done is clicked
  const [paletteVersion, setPaletteVersion] = useState(0);

  const handlePaletteClose = useCallback(() => {
    setPaletteVersion(prev => prev + 1);
  }, []);

  const handleNoteSaveSuccess = useCallback(
    (id: string | number, noteContent: string, createdNote?: UserNoteGet) => {
      const noteId = createdNote?.id;
      if (createdNote?.attio_valuation_id) {
        updateValuationNotes(createdNote.attio_valuation_id, noteContent, noteId);
      } else {
        updateValuationNotes(id as string, noteContent, noteId);
      }
    },
    [updateValuationNotes]
  );

  const { isNoteLoading, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: handleNoteSaveSuccess,
  });

  // When grouped, all valuations for lookups/selection order come from groupedRows
  const flatValuationsForLookup = useMemo(
    () => (groupByDeals ? groupedRows.flatMap(g => g.valuations) : rows),
    [groupByDeals, groupedRows, rows]
  );

  const handleNoteSave = useCallback(
    async (valuationId: string, noteContent: string, currentNote?: string) => {
      const request: CreateNoteRequest = {
        content: noteContent,
        attio_valuation_id: valuationId,
        reference_type: 'at',
      };

      const currentValuation = flatValuationsForLookup.find(r => r.record_id === valuationId);
      // A note DB record exists if note is non-null (regardless of content being empty)
      const hasExistingNote = currentValuation?.note != null;

      const existingNoteId =
        hasExistingNote && currentValuation?.note?.id ? currentValuation.note.id.toString() : undefined;

      await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
    },
    [flatValuationsForLookup, handleNoteSaveGeneric]
  );

  const getDefaultVisibility = useCallback((): Record<string, boolean> => {
    const initialVisibility: Record<string, boolean> = {};
    valuationsColumns.forEach(col => {
      initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
    });
    return initialVisibility;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storageKey = sectionId ? `valuations_column_visibility_${sectionId}` : '';
  const [
    columnVisibility,
    setColumnVisibility,
    isColumnVisibilityReady,
  ] = useLocalStorageByUser<Record<string, boolean>>(
    storageKey || 'temp_key',
    getDefaultVisibility()
  );

  const columnOrderStorageKey = sectionId
    ? `valuations_column_order_${sectionId}`
    : 'valuations_column_order';
  const [columnOrder, setColumnOrder, isColumnOrderReady] = useLocalStorageByUser<string[]>(
    columnOrderStorageKey,
    [...DEFAULT_COLUMN_ORDER]
  );

  const resolvedColumnOrder = useMemo(() => resolveColumnOrder(columnOrder), [columnOrder]);

  const [columnWidths, setColumnWidths, isColumnWidthsReady] = useLocalStorageByUser<Record<string, number>>(
    'columnWidths',
    {}
  );

  // Restore view settings once server views and IndexedDB preference are both ready.
  useEffect(() => {
    if (!isViewsReady || !isActiveViewReady || hasRestoredActiveView) {
      return;
    }
    setRestoredSectionId(sectionId);
    if (activeViewId === 'default') {
      setColumnVisibility(getDefaultVisibility());
      setColumnOrder([...DEFAULT_COLUMN_ORDER]);
      setGroupByDeals(false);
    } else {
      const view = views.find(v => v.id === activeViewId);
      if (view) {
        setColumnVisibility({ ...(view.columnVisibility ?? {}) });
        setColumnOrder(resolveColumnOrder(view.columnOrder));
        setGroupByDeals(view.groupByDeals ?? false);
      } else {
        // View was deleted; fall back to default
        setActiveViewId('default');
        setColumnVisibility(getDefaultVisibility());
        setColumnOrder([...DEFAULT_COLUMN_ORDER]);
        setGroupByDeals(false);
      }
    }
  }, [
    isViewsReady, isActiveViewReady, hasRestoredActiveView,
    activeViewId, views, sectionId,
    getDefaultVisibility, setColumnVisibility, setColumnOrder, setGroupByDeals, setActiveViewId,
  ]);

  const handleColumnWidthChange = useCallback(
    (columnId: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth,
      }));
    },
    [setColumnWidths]
  );

  const handleColumnReorder = useCallback(
    (sourceColumn: string, targetColumn: string) => {
      const order = resolveColumnOrder(columnOrder);
      const sourceIdx = order.indexOf(sourceColumn);
      const targetIdx = order.indexOf(targetColumn);
      if (sourceIdx === -1 || targetIdx === -1 || sourceIdx === targetIdx) {
        return;
      }
      const newOrder = arrayMove(order, sourceIdx, targetIdx);
      setColumnOrder(newOrder);

      if (activeViewId !== 'default') {
        const view = views.find(v => v.id === activeViewId);
        if (view) {
          updateView({
            ...view,
            columnVisibility,
            columnOrder: newOrder,
          });
        }
      }
    },
    [columnOrder, setColumnOrder, activeViewId, views, updateView, columnVisibility]
  );

  const conditionalColumns = useMemo(() => {
    const columnById = Object.fromEntries(valuationsColumns.map(col => [col.id, col]));
    return resolvedColumnOrder
      .filter(id => columnVisibility[id] !== false)
      .map(id => columnById[id])
      .filter((col): col is (typeof valuationsColumns)[number] => Boolean(col));
  }, [resolvedColumnOrder, columnVisibility]);

  // Check if any financial column is visible (triggers horizontal scroll)
  const hasExtraColumns = useMemo(
    () => conditionalColumns.some(col => FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(col.id)),
    [conditionalColumns]
  );

  const allRows = rows;

  const [filteredRows, setFilteredRows] = useState<SimplifiedAttioValuation[]>(allRows);

  // Clear stale rows immediately when switching sections so the spreadsheet shows loading state
  useEffect(() => {
    setFilteredRows([]);
  }, [sectionId]);

  const orderedRowsForSelection = groupByDeals ? flatValuationsForLookup : filteredRows;
  useEffect(() => {
    onVisibleValuationsChange?.(orderedRowsForSelection);
  }, [onVisibleValuationsChange, orderedRowsForSelection]);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  const rowsForColumnWidth = groupByDeals ? flatValuationsForLookup : filteredRows;
  const handleAutoFitColumns = useCallback(() => {
    const longestCompanyName = rowsForColumnWidth.reduce((longest, row) => {
      const name = row.company_name || '';
      return name.length > longest.length ? name : longest;
    }, '');

    const charWidth = 8;
    const padding = 32;
    const companyNameWidth = Math.max(150, Math.min(400, longestCompanyName.length * charWidth + padding));

    setColumnWidths({
      [COMPANY_NAME_KEY]: companyNameWidth,
    });
  }, [setColumnWidths, rowsForColumnWidth]);

  const { isSearching } = useAttioFilters(allRows, setFilteredRows);

  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const { data: dealStageOptionsData = [] } = useQuery<Array<{ id?: { status_id?: string }; title?: string }>>({
    queryKey: [AttioQueryKeys.DEAL_STAGE_OPTIONS],
    queryFn: async () => {
      const response = await getDealStatusOptions();
      return (response.data || []) as Array<{ id?: { status_id?: string }; title?: string }>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const statusOptions = useMemo(
    () => attioStatusOptions.map(option => ({
      id: {
        workspace_id: option.id.workspace_id,
        object_id: option.id.object_id,
        attribute_id: option.id.attribute_id,
        option_id: option.id.option_id,
      },
      title: option.title,
      is_archived: option.is_archived,
    })),
    [attioStatusOptions]
  );
  const statusOptionsLoading = isLoadingStatusOptions;

  const user = useSelector((state: RootStateType) => state.user);
  const { zoomLevel } = useZoom();

  // Every ancestor up to the layout is min-height only, so `flex: 1` resolves against
  // content rather than the screen. The virtualized sheet needs an explicit,
  // zoom-corrected height to measure.
  const tablePanelSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      height: calculateTableHeight(zoomLevel, 190),
      minHeight: '560px',
      overflow: 'hidden',
    }),
    [zoomLevel]
  );

  const { setSecondaryNavActionsNode, setTertiaryNavNode } = useCompanyDashboardContext();

  const currentUserDisplayName = useMemo(
    () => [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'User',
    [user?.email, user?.first_name, user?.last_name]
  );

  const userSelectorNode = useMemo(
    () =>
      user?.is_staff ? (
        <WorkboardUserSelector
          currentUserDisplayName={currentUserDisplayName}
          isLoading={isLoadingStaffUsers}
          selectedUserEmail={selectedUserEmail}
          setSelectedUserEmail={setSelectedUserEmail}
          staffUsers={staffUsers}
        />
      ) : null,
    [currentUserDisplayName, isLoadingStaffUsers, selectedUserEmail, setSelectedUserEmail, staffUsers, user?.is_staff]
  );

  useEffect(() => {
    setSecondaryNavActionsNode?.(userSelectorNode);
    return () => {
      setSecondaryNavActionsNode?.(null);
    };
  }, [setSecondaryNavActionsNode, userSelectorNode]);

  // Subscribe to valuation updates from OperationsModal
  useEventSubscription<ValuationsTableValuationUpdatedPayload>(
    EventType.WORKBOARD_VALUATION_UPDATED,
    useCallback(
      (payload: ValuationsTableValuationUpdatedPayload) => {
        const { valuationId, updates } = payload;

        const applyUpdatesToRow = (row: SimplifiedAttioValuation): SimplifiedAttioValuation => {
          if (row.record_id !== valuationId) {
            return row;
          }
          const updatedRow = { ...row };
          if (updates.status) {
            updatedRow.status = updates.status;
          }
          if (updates.dealStage) {
            updatedRow.deal_stage = updates.dealStage;
          }
          if (updates.deal) {
            if (updates.deal.value) {
              updatedRow.deal_value = updates.deal.value;
            }
            if (updates.deal.due) {
              updatedRow.deal_due = updates.deal.due;
            }
            if (updates.deal.stage) {
              updatedRow.deal_stage = updates.deal.stage;
            }
          }
          return updatedRow;
        };

        setFilteredRows(prevRows => prevRows.map(applyUpdatesToRow));
        updateValuationStatus(valuationId, updates.status || '');
      },
      [updateValuationStatus]
    )
  );

  const handleDealStageChange = useCallback(
    async (row: SimplifiedAttioValuation, dealStageId: string) => {
      if (!row.deal_id) {
        enqueueSnackbar('Cannot update deal stage: deal_id is missing', { variant: 'error' });
        return;
      }

      const dealId = row.deal_id;
      try {
        await updateValuationDealStage(dealId, dealStageId, row.company_name ?? undefined);

        // Find the deal stage label from the options (same structure as ValuationDealInfo expects)
        const selectedOption = dealStageOptionsData.find(option => option.id?.status_id === dealStageId);
        const newDealStage = selectedOption?.title || row.deal_stage;

        // Update local state
        setFilteredRows(prevRows =>
          prevRows.map(r => (r.record_id === row.record_id ? { ...r, deal_stage: newDealStage } : r))
        );
      } catch (error) {
        Logger.error('Error updating deal stage', error);
      }
    },
    [dealStageOptionsData]
  );

  const handleRowClick = useCallback(
    (row: SimplifiedAttioValuation, event: React.MouseEvent) => {
      onRowClick(row, event as React.MouseEvent<HTMLTableRowElement>);
    },
    [onRowClick]
  );

  const handleStatusUpdate = useCallback(
    (row: SimplifiedAttioValuation) => {
      updateValuationStatus(row.record_id, row.status || '');
    },
    [updateValuationStatus]
  );

  const visibleColumnIds = useMemo(
    () => conditionalColumns.map(col => col.id),
    [conditionalColumns]
  );

  const handleToggleCollapsedDeal = useCallback((dealKey: string) => {
    setCollapsedDealIds(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(dealKey) ? current.filter(id => id !== dealKey) : [...current, dealKey];
    });
  }, [setCollapsedDealIds]);

  const handleAddView = useCallback(() => {
    setPendingView(null);
    setIsViewDialogOpen(true);
  }, []);

  const handleCloseViewDialog = useCallback(() => {
    setIsViewDialogOpen(false);
    setPendingView(null);
  }, []);

  const handleCreateView = useCallback(
    async (view: ValuationView) => {
      const created = await createView(view);
      setActiveViewId(created.id);
      setColumnVisibility({ ...(created.columnVisibility ?? {}) });
      setColumnOrder(resolveColumnOrder(created.columnOrder));
      setGroupByDeals(created.groupByDeals ?? false);
    },
    [createView, setActiveViewId, setColumnVisibility, setColumnOrder, setGroupByDeals]
  );

  const handleTabChange = useCallback(
    (viewId: string) => {
      setActiveViewId(viewId);
      if (viewId === 'default') {
        setColumnVisibility(getDefaultVisibility());
        setColumnOrder([...DEFAULT_COLUMN_ORDER]);
        setGroupByDeals(false);
      } else {
        const view = views.find(v => v.id === viewId);
        if (view) {
          setColumnVisibility({ ...(view.columnVisibility ?? {}) });
          setColumnOrder(resolveColumnOrder(view.columnOrder));
          setGroupByDeals(view.groupByDeals ?? false);
        }
      }
    },
    [views, getDefaultVisibility, setActiveViewId, setColumnVisibility, setColumnOrder, setGroupByDeals]
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuViewId(null);
  }, []);

  const handleItemMenuClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
      event.stopPropagation();
      setMenuViewId(item.value);
      setMenuAnchor(event.currentTarget);
    },
    []
  );

  const handleEditView = useCallback(() => {
    handleMenuClose();
    const view = views.find(v => v.id === menuViewId);
    if (view) {
      setPendingView(view);
      setIsViewDialogOpen(true);
    }
  }, [handleMenuClose, menuViewId, views]);

  const handleSaveEditedView = useCallback(
    async (updated: ValuationView) => {
      const saved = await updateView(updated);
      if (activeViewId === saved.id) {
        setColumnVisibility({ ...(saved.columnVisibility ?? {}) });
        setColumnOrder(resolveColumnOrder(saved.columnOrder));
        setGroupByDeals(saved.groupByDeals ?? false);
      }
    },
    [updateView, activeViewId, setColumnVisibility, setColumnOrder, setGroupByDeals]
  );

  const handleViewDialogSubmit = useCallback(
    async (view: ValuationView) => {
      if (pendingView) {
        await handleSaveEditedView(view);
      } else {
        await handleCreateView(view);
      }
    },
    [pendingView, handleCreateView, handleSaveEditedView]
  );

  const handleCloneView = useCallback(async () => {
    handleMenuClose();
    if (!menuViewId) {
      return;
    }
    const source = views.find(v => v.id === menuViewId);
    if (!source) {
      return;
    }
    const existingNames = new Set(views.map(v => v.name));
    let cloneName = `${source.name} (Copy)`;
    let i = 2;
    while (existingNames.has(cloneName)) {
      cloneName = `${source.name} (Copy ${i})`;
      i += 1;
    }
    await cloneView(source, cloneName);
  }, [handleMenuClose, menuViewId, cloneView, views]);

  const handleDeleteView = useCallback(async () => {
    handleMenuClose();
    if (!menuViewId) {
      return;
    }
    await deleteView(menuViewId);
    if (activeViewId === menuViewId) {
      handleTabChange('default');
    }
  }, [handleMenuClose, menuViewId, deleteView, activeViewId, handleTabChange]);

  const tertiaryItems = useMemo<TertiaryNavigationItem[]>(
    () => [
      DEFAULT_TERTIARY_ITEM,
      ...views.map(v => ({ label: v.name, value: v.id, showMenu: true, isClosable: true })),
    ],
    [views]
  );

  const tertiaryLeftActions = useMemo(
    () => <TertiaryNavigationAddButton onClick={handleAddView} />,
    [handleAddView]
  );

  const tertiaryNavActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        {toolbarPrefix}
        <ConfigToggler onPaletteClose={handlePaletteClose} />
        {!hideTable && (
          <TertiaryNavigationIconButton
            ariaLabel="Auto-fit columns"
            icon={<FitScreenOutlinedIcon />}
            tooltipTitle="Auto-fit Columns"
            variant="toolbar"
            onClick={handleAutoFitColumns}
          />
        )}
        <ExpandableSearch
          isValuationsPage
          ref={searchRef}
          value={searchParam.searchParam}
          onChange={e => setSearchParam(prev => ({ ...prev, searchParam: e.target.value }))}
          placeholder="Search by company..."
          searchAll={searchParam.searchAll}
          onSearchAllChange={v => setSearchParam(prev => ({ ...prev, searchAll: v }))}
        />
        <TertiaryNavigationIconButton
          ariaLabel="Refresh data"
          icon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          tooltipTitle="Refresh data"
          variant="toolbar"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true);
            refetchValuations();
          }}
        />
        {isLoading || isRefreshing ? (
          <Skeleton variant="rounded" width={140} height={28} sx={{ borderRadius: '8px' }} />
        ) : (
          <Box sx={{
            backgroundColor: 'action.hover',
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Total Valuations: {totalCountFromHook.toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>
    ),
    [toolbarPrefix, handlePaletteClose, hideTable, handleAutoFitColumns, searchParam, isRefreshing, refetchValuations, isLoading, totalCountFromHook]
  );

  const arePreferencesReady =
    isViewsReady &&
    isActiveViewReady &&
    hasRestoredActiveView &&
    isGroupByDealsReady &&
    areCollapsedDealsReady &&
    isSelectedUserReady &&
    isColumnVisibilityReady &&
    isColumnOrderReady &&
    isColumnWidthsReady &&
    isPaletteReady;

  const tertiaryNavNode = useMemo(
    () => arePreferencesReady ? (
      <TertiaryNavigation
        items={tertiaryItems}
        leftActions={tertiaryLeftActions}
        actions={tertiaryNavActionsNode}
        value={activeViewId}
        onChange={handleTabChange}
        onItemClose={async (_, item) => {
          await deleteView(item.value);
          if (activeViewId === item.value) {
            handleTabChange('default');
          }
        }}
        onItemMenuClick={handleItemMenuClick}
        zoomLevel={zoomLevel}
      />
    ) : null,
    [arePreferencesReady, tertiaryNavActionsNode, zoomLevel, tertiaryItems, tertiaryLeftActions, activeViewId, handleTabChange, deleteView, handleItemMenuClick]
  );

  useEffect(() => {
    setTertiaryNavNode?.(tertiaryNavNode);
    return () => {
      setTertiaryNavNode?.(null);
    };
  }, [setTertiaryNavNode, tertiaryNavNode]);

  if (!arePreferencesReady) {
    return <LoadingSection />;
  }

  return (
    <>
      <ValuationsNewViewDialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        onCreateView={handleViewDialogSubmit}
        currentColumnVisibility={columnVisibility}
        currentColumnOrder={resolvedColumnOrder}
        currentGroupByDeals={groupByDeals as boolean}
        defaultName={defaultNewViewName}
        initialView={pendingView ?? undefined}
      />
      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuViewId)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <PrimaryNavigationMenuItem icon={<EditIcon fontSize="inherit" />} onClick={handleEditView}>
          Edit View
        </PrimaryNavigationMenuItem>
        <PrimaryNavigationMenuItem icon={<ContentCopyIcon fontSize="inherit" />} onClick={handleCloneView}>
          Clone
        </PrimaryNavigationMenuItem>
        <Divider />
        <PrimaryNavigationMenuItem icon={<DeleteIcon fontSize="inherit" />} onClick={handleDeleteView} tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
      {!hideTable && (
        <Box sx={tablePanelSx}>
          <WorkboardSpreadsheet
            key={paletteVersion}
            valuations={groupByDeals ? flatValuationsForLookup : filteredRows}
            visibleColumnIds={visibleColumnIds}
            isLoading={isLoading || isRefreshing}
            columnWidths={columnWidths}
            statusOptions={statusOptions}
            statusOptionsLoading={statusOptionsLoading}
            isNoteLoading={isNoteLoading}
            dealStageOptions={dealStageOptionsData as DealStageOption[]}
            sortCriteria={sortCriteria}
            dealGroups={groupByDeals ? groupedRows : undefined}
            collapsedDealIds={collapsedDealIds}
            onToggleCollapsedDeal={handleToggleCollapsedDeal}
            onRequestSort={onRequestSort}
            onColumnReorder={handleColumnReorder}
            onColumnResize={handleColumnWidthChange}
            onRowClick={handleRowClick}
            onStatusUpdate={handleStatusUpdate}
            onNoteSave={handleNoteSave}
            onEngagementManagerUpdate={updateValuationEngagementManager}
            onOtherConsultantsUpdate={updateValuationOtherConsultants}
            onDealStageChange={handleDealStageChange}
          />
        </Box>
      )}
    </>
  );
};
